import { Company, logoUrl } from '@ecountabl/lib'
import { extra } from '../config'
import React, { useState, useEffect, useContext } from 'react'
import { ImageStyle, Platform, View } from 'react-native'
import Loader from './loader'
import { SvgCss } from 'react-native-svg/css'
import { Image, log } from './pwa'
import { ShadowView } from '../style'
import { ThemeContext } from './context/ThemeContext'
import { MaterialIcons } from '@expo/vector-icons'

type Props = {
  company: Company
  maxSize?: boolean
  style?: ImageStyle
  priority?: boolean // used for nextJS LCP rendering
}

const Logo: React.FC<Props> = (props: Props) => {
  const { maxSize, company: { name }, priority } = props
  const style = props.style ?? {}

  const { theme } = useContext(ThemeContext)
  const { white, dark } = theme.colors
  const { width, height } = style
  const svgWidth = (
    typeof style.width === 'number' || typeof style.width === 'string'
  ) ? style.width : '100%'
  const svgHeight = (
    typeof style.height === 'number' || typeof style.height === 'string'
  ) ? style.height : '100%'

  const isImpactNetwork = props.company._id?.startsWith('IMPACT_NETWORK') ?? false

  const uri = isImpactNetwork ?
    (props.company.logourl || null)
    : logoUrl(props.company, extra.cdn)

  const ext = uri?.split('.').pop()

  const [svgxml, setSvgxml] = useState('')
  const [loading, setLoading] = useState(false)
  const [showFallback, setShowFallback] = useState(false)

  useEffect(() => {
    // fetching SVG separately isn't required for nextjs/web, the svg URIs "just work"
    if (Platform.OS !== 'web') {
      const fetchSvgXml = (): Promise<void> => {
        return fetch(uri)
          .then(res => res.text())
          .then(xml => setSvgxml(xml))
          .catch(ex => {
            log.error(ex, { tags: { message: 'error loading svg xml' } })
            setShowFallback(true)
          }).then(() => { setLoading(false) })
      }

      if (ext === 'svg') {
        setLoading(true)
        fetchSvgXml()
      }
    }
  }, [ext, uri])

  if (loading) {
    return <Loader />
  }

  if (!uri || showFallback) {
    const fallbackIconSize = typeof width === 'number' ? width / 2 : 40
    return (
      <ShadowView
        style={[
          {
            backgroundColor: white,
            borderRadius: style.borderRadius,
            justifyContent: 'center',
            alignItems: 'center',
            width: style.width || 150,
            height: style.height || 150
          },
          style
        ]}
      >
        <MaterialIcons
          name="storefront"
          size={fallbackIconSize}
          color={dark}
        />
      </ShadowView>
    )
  }

  return (
    <ShadowView style={{ borderRadius: style.borderRadius }}>
      {
        svgxml ? (
          <View
            style={{
              width: svgWidth,
              height: svgHeight,
              backgroundColor: white,
              borderRadius: style.borderRadius,
            }}
          >
            <SvgCss
              xml={svgxml}
              height={svgHeight}
              width={svgWidth}
            />
          </View>
        ) : (
          <Image
            key={uri}
            source={{ uri }}
            alt={`${name} logo`}
            onError={({ nativeEvent: { error } }): void => {
              log.error(error, { tags: { message: 'error loading logo', uri} })
              setShowFallback(true)
            }}
            resizeMode='contain'
            priority={priority}
            style={[
              {
                height: (height || 150),
                width: (width || 150),
                resizeMode: 'contain',
                borderRadius: style.borderRadius,
                backgroundColor: white,
              },
              style,
              maxSize && height && width ?
                { height: height as number, width: width as number }
                : null
            ]}
          />
        )
      }
    </ShadowView>
  )
}

export default Logo